import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import Link from 'next/link';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Safely converts HTML tags to React elements
 * This is much safer than dangerouslySetInnerHTML and has zero bundle overhead
 * 
 * Currently supports:
 * - <strong>text</strong> → bold text
 * - <a href="url">text</a> → Next.js Link component (for internal navigation)
 * 
 * Usage examples:
 * - parseHtmlToReact("This is <strong>bold</strong> text.")
 * - parseHtmlToReact("Visit <a href='/pricing'>our pricing page</a> for more info.")
 * 
 * Note: Links use Next.js Link component for optimal performance and client-side navigation
 */
export function parseHtmlToReact(text: string): React.ReactNode {
  if (!text) return text;
  
  // Simple approach: handle each tag type separately
  let result = text;
  
  // Handle <strong> tags - more robust regex
  result = result.replace(/<strong>([^<]*)<\/strong>/g, (match, content) => {
    return `|||STRONG|||${content}|||/STRONG|||`;
  });
  
  // Handle <a> tags (simple version with just href) - support both single and double quotes
  result = result.replace(/<a href=['"]([^'"]*)['"]>(.*?)<\/a>/g, (match, href, content) => {
    return `|||LINK|||${href}|||CONTENT|||${content}|||/LINK|||`;
  });
  
  // Split by markers and convert to React elements
  const parts = result.split(/(\|\|\|[A-Z/]+\|\|\|)/g);
  
  return parts.map((part, index) => {
    if (!part) return null;
    
    // Handle strong tags - check for the complete marker pattern
    if (part === '|||STRONG|||') {
      // Find the content in the next part
      const content = parts[index + 1] || '';
      return React.createElement('strong', { key: index }, content);
    }
    
    // Handle link tags - check for the complete marker pattern
    if (part === '|||LINK|||') {
      // Find the href and content in the next parts
      const href = parts[index + 1] || '';
      const content = parts[index + 3] || ''; // Skip |||CONTENT|||
      
      return React.createElement(Link, {
        key: index,
        href,
        className: 'text-red-500 hover:text-primary-hover'
      }, content);
    }
    
    // Skip marker parts and content parts that are already handled
    if (part === '|||/STRONG|||' || part === '|||CONTENT|||' || part === '|||/LINK|||') {
      return null;
    }
    
    // Skip content that's already been processed by a tag
    const prevPart = parts[index - 1];
    if (prevPart === '|||STRONG|||' || prevPart === '|||LINK|||' || prevPart === '|||CONTENT|||') {
      return null;
    }
    
    return part;
  }).filter(Boolean);
}
